import type { CSSProperties } from 'react';
import type { SxProps, Theme } from '@mui/material';
import { AI_GRADIENT_CSS, aiGradientTextSx } from 'common/constants/ai';

export const popperSx: SxProps<Theme> = { zIndex: 1300 };

// Action button shared styles
export const actionButtonSx: SxProps<Theme> = {
  borderRadius: '6px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.75rem',
  color: '#333',
  border: '1px solid #E9E9E9 !important',
  backgroundColor: '#fff',
  px: 1.5,
  py: 0.25,
  minHeight: 0,
  whiteSpace: 'nowrap',
  '&:hover': {
    border: '1px solid #d5d5d5 !important',
    backgroundColor: '#f5f5f5',
  },
};

// Gradient divider
export const gradientDividerSx: SxProps<Theme> = {
  border: 'none',
  height: '1.5px',
  background: AI_GRADIENT_CSS,
  opacity: 0.4,
};

// Gradient dashed chip trigger
export const chipTriggerSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  position: 'relative',
  height: 32,
  padding: '0 12px',
  borderRadius: '16px',
  cursor: 'pointer',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(79, 85, 231, 0.06)',
  },
};

// SVG overlay for gradient dashed border
export const svgBorderStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
};

// Chip gradient text
export const chipTextSx: SxProps<Theme> = {
  fontWeight: 500,
  fontSize: '0.8125rem',
  lineHeight: 1,
  ...aiGradientTextSx as Record<string, unknown>,
};

// Expanded Paper
export const expandedPaperSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '10px',
  boxShadow: '0px 4px 4px 0px #00000040',
  width: 420,
  py: 1,
  px: 2,
  gap: 1,
};

// Expanded header row
export const expandedHeaderSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

// Expanded AI icon
export const expandedIconSx: SxProps<Theme> = {
  width: 40,
  height: 40,
  minWidth: 40,
  objectFit: 'contain',
};

// Expanded title
export const expandedTitleSx: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: '0.85rem',
  lineHeight: 1.3,
};

// Expanded subtitle colored spans
export const documentColorSx: SxProps<Theme> = {
  color: '#08B7CF',
  fontWeight: 500,
};

export const referenceTypeColorSx: SxProps<Theme> = {
  color: '#6C63FF',
  fontWeight: 500,
};

// Commentary "Why this suggestion?" text
export const commentaryTitleSx: SxProps<Theme> = {
  fontStyle: 'italic',
  color: 'text.secondary',
  fontSize: '0.8rem',
};

// Commentary bullet items
export const commentaryItemSx: SxProps<Theme> = {
  lineHeight: 1.5,
  fontSize: '0.8rem',
};

// Expanded action buttons container
export const expandedButtonsContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  gap: 1.5,
};

// Compact action buttons container
export const compactButtonsContainerSx: SxProps<Theme> = {
  display: 'flex',
  gap: 0.75,
};

// Compact Paper
export const compactPaperSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  borderRadius: '24px',
  boxShadow: '0px 4px 4px 0px #00000040',
  pt: '6px',
  pb: '6px',
  pr: '16px',
  pl: '4px',
};

// Compact AI icon
export const compactIconSx: SxProps<Theme> = {
  width: 42,
  height: 42,
  minWidth: 42,
  objectFit: 'contain',
  mb: '-4px',
};

// Compact right content column
export const compactContentSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  flex: 1,
};

// "Why this suggestion?" text in compact view
export const whySuggestionTextSx: SxProps<Theme> = {
  fontStyle: 'italic',
  color: 'text.secondary',
  fontSize: '0.75rem',
  textAlign: 'center',
};

// Clickable "suggestion" word
export const suggestionLinkSx: SxProps<Theme> = {
  fontStyle: 'italic',
  fontSize: '0.75rem',
  cursor: 'pointer',
  ...aiGradientTextSx as Record<string, unknown>,
  '&:hover': {
    textDecoration: 'underline',
  },
};

// Accept icon color
export const acceptIconSx: SxProps<Theme> = {
  fontSize: 14,
  color: '#4caf50',
};

// Decline icon color
export const declineIconSx: SxProps<Theme> = {
  fontSize: 14,
  color: '#e91e63',
};
